import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest, ApiRequestError } from '../api/client.js';
import { useSession } from '../auth/SessionProvider.js';
import { Alert, Button, Field, TextInput } from '../components/ui.js';
import { MIN_PASSWORD_LENGTH_HINT } from '../lib/passwordHint.js';

export function ChangePasswordPage() {
  const { user, refresh } = useSession();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('The two new passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await apiRequest('/api/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      await refresh();
      navigate('/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Your password could not be changed. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <h1 className="mb-5 text-center text-xl font-semibold text-slate-900">
          {user?.mustChangePassword ? 'Set a new password' : 'Change your password'}
        </h1>

        <form
          onSubmit={(event) => void handleSubmit(event)}
          className="flex flex-col gap-4 rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200"
        >
          {user?.mustChangePassword && (
            <Alert variant="warning">Your password must be changed before you can continue.</Alert>
          )}
          {error && <Alert variant="error">{error}</Alert>}

          <Field label="Current password" htmlFor="current" required>
            <TextInput
              id="current"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </Field>

          <Field label="New password" htmlFor="new" required hint={MIN_PASSWORD_LENGTH_HINT}>
            <TextInput
              id="new"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </Field>

          <Field label="Confirm new password" htmlFor="confirm" required>
            <TextInput
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </Field>

          <p className="text-xs text-slate-600">
            Changing your password signs you out of every other device.
          </p>

          <Button type="submit" loading={submitting} className="w-full">
            Change password
          </Button>
        </form>
      </div>
    </div>
  );
}
